/**
 * Start screen (docs/GAME_DESIGN.md § 8): the bouncy wordmark, the player's
 * level and theme, one big Play (no level select — the game picks the level)
 * and small buttons for Leaderboards, Settings and Profile. The menu banner, if
 * any, sits below the menu, well away from Play.
 */
import { useEffect, useRef } from 'react';
import type { Locale, ThemePack } from '@dropstack/core';
import type { AdsService } from '../contracts.ts';
import { useT } from '../i18n/index.ts';
import { AdSlot } from '../components/AdSlot.tsx';
import { Button } from '../components/Button.tsx';
import { Icon } from '../components/Icon.tsx';
import { LevelChip } from '../components/LevelChip.tsx';
import { Wordmark } from '../components/Wordmark.tsx';
import './start.css';

export type StartDestination = 'leaderboards' | 'settings' | 'profile';

export interface StartScreenProps {
  level: number;
  theme: ThemePack;
  locale: Locale;
  /** Play was pressed; the run is being prepared. */
  starting: boolean;
  /** The offline demo backend is in use (scores stay on this device). */
  offlineDemo: boolean;
  ads: AdsService;
  adsEnabled: boolean;
  /**
   * Move focus to Play when the screen appears: true when the player comes
   * back from another screen (whose focused element is gone), false on launch.
   */
  focusPlay: boolean;
  onPlay(): void;
  onOpen(destination: StartDestination): void;
}

export function StartScreen(props: StartScreenProps) {
  const { level, theme, locale, starting, offlineDemo, ads, adsEnabled, focusPlay, onPlay, onOpen } = props;
  const t = useT();
  const playRef = useRef<HTMLButtonElement>(null);

  // Mount-only on purpose: focus moves once, when the screen appears.
  const focusOnMount = useRef(focusPlay);
  useEffect(() => {
    if (focusOnMount.current) playRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <main className="start" aria-labelledby="start-title">
      <div className="start__hero">
        <h1 id="start-title" className="start__title">
          <Wordmark />
        </h1>
        <p className="start__tagline">{t('shell.startTagline')}</p>
      </div>

      <nav className="start__menu" aria-label={t('shell.startMenuLabel')}>
        <LevelChip level={level} theme={theme} locale={locale} />
        <Button
          ref={playRef}
          variant="play"
          size="xl"
          icon="play"
          busy={starting}
          className="start__play"
          onClick={onPlay}
          aria-describedby="start-play-hint"
        >
          {starting ? t('shell.startGettingReady') : t('shell.startPlay')}
        </Button>
        <p id="start-play-hint" className="start__hint">
          {t('shell.startPlayHint')}
        </p>
        <div className="start__secondary">
          <Button icon="trophy" disabled={starting} onClick={() => onOpen('leaderboards')}>
            {t('shell.navLeaderboards')}
          </Button>
          <Button icon="settings" disabled={starting} onClick={() => onOpen('settings')}>
            {t('shell.navSettings')}
          </Button>
          <Button icon="user" disabled={starting} onClick={() => onOpen('profile')}>
            {t('shell.navProfile')}
          </Button>
        </div>
        {offlineDemo && (
          <p className="start__offline">
            <Icon name="wifi-off" />
            {t('shell.startOffline')}
          </p>
        )}
      </nav>

      <div className="start__ad">
        <AdSlot ads={ads} slot="menu-banner" enabled={adsEnabled} />
      </div>
    </main>
  );
}
